import type React from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ChevronUp, Volume2 } from 'lucide-react';
import type { DeviceKind } from '@/hooks/use-media-devices';

/**
 * Compact dropdown menu listing devices of a single kind (mic / camera /
 * speaker). The trigger is a small chevron next to the matching toggle
 * button (mic / camera) or, for the speaker, an icon button of its own.
 *
 * Opening the menu lazily calls `onEnsureLabels` — without media permission
 * `enumerateDevices()` returns entries with empty `label` fields, and
 * calling getUserMedia from a click counts as a user gesture so it isn't
 * anti-spam-blocked.
 *
 * Shared between the in-call control bar (`InCallWindow`) and the pre-call
 * lobby (`PreCallLobby`) so the two surfaces present an identical picker UX.
 */
export function DeviceMenu({
  kind,
  devices,
  currentId,
  onSelect,
  onEnsureLabels,
  disabled,
  unsupported,
  unsupportedReason,
  triggerLabel,
  triggerVariant,
  trigger,
  hasLabels,
}: {
  kind: DeviceKind;
  devices: MediaDeviceInfo[];
  currentId: string | undefined;
  onSelect: (deviceId: string) => void;
  onEnsureLabels: () => void;
  disabled: boolean;
  unsupported?: boolean;
  unsupportedReason?: string;
  triggerLabel: string;
  triggerVariant: 'chevron' | 'button';
  trigger?: React.ReactNode;
  hasLabels: boolean;
}) {
  const triggerNode =
    triggerVariant === 'chevron' ? (
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className="h-9 w-5 px-0 -ml-px rounded-l-none"
        disabled={disabled || unsupported}
        title={unsupported ? unsupportedReason : `Choose ${triggerLabel.toLowerCase()}`}
        data-testid={`device-picker-${kind}`}
      >
        <ChevronUp className="h-3 w-3" />
      </Button>
    ) : (
      trigger ?? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          disabled={disabled || unsupported}
          title={unsupported ? unsupportedReason : triggerLabel}
          data-testid={`device-picker-${kind}`}
        >
          <Volume2 className="h-4 w-4" />
        </Button>
      )
    );

  if (unsupported) return triggerNode;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open && !hasLabels) onEnsureLabels();
      }}
    >
      <DropdownMenuTrigger asChild>{triggerNode}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        side="top"
        className="w-72 z-[10001]"
      >
        <DropdownMenuLabel>{triggerLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {devices.length === 0 ? (
          <DropdownMenuItem disabled>No devices found</DropdownMenuItem>
        ) : (
          devices.map((d) => {
            const isActive =
              d.deviceId === currentId ||
              (!currentId && d.deviceId === 'default');
            return (
              <DropdownMenuCheckboxItem
                key={d.deviceId || d.label}
                checked={isActive}
                onSelect={(e) => {
                  e.preventDefault();
                  onSelect(d.deviceId);
                }}
                data-testid={`device-option-${kind}-${d.deviceId || 'unknown'}`}
              >
                <span className="truncate">
                  {d.label || `${triggerLabel} (unnamed)`}
                </span>
              </DropdownMenuCheckboxItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
